/* eslint-disable react-hooks/exhaustive-deps */

import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Divider, Form, Row, Space, Typography } from "antd";
import Img from "assets/images";
import CustomButton from "components/CustomButton";
import CustomCard from "components/CustomCard";
import CustomDrawer from "components/CustomDrawer";
import CustomModal from "components/CustomModal";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import FormInput from "components/FormInput";
import { INPUT_TYPE } from "config/constants";
import { useUpdateProduct } from "hooks/product/product";
import { map } from "lodash";
import { DEPOSIT_PAYMENT_METHOD_TYPE, PRODUCT_FULFILLMENT_MODE, VAT_TYPE } from "pages/App/Product/constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notify, validator } from "utils/helperFuncs";
import CustomModalExportVat from "../CustomModalExportVat";

import ProductSoldDisplay from "../ProductSoldDisplay";

import "./index.scss";

const convertWarrantyToMonths = (warranty, warrantyUnit) => {
  if (warrantyUnit === "YEAR") {
    return warranty * 12;
  }
  return warranty;
};

const getStandardWarrantyInMonths = (productWarrantyPrices) => {
  const standardWarranty = productWarrantyPrices?.find((item) => item.level === 1);
  if (!standardWarranty) return 12; // Fallback
  return convertWarrantyToMonths(standardWarranty.warranty, standardWarranty.warrantyUnit);
};

const inputOptionsPrices = {
  min: 0,
  maxLength: 11,
  style: { width: "100%" },
  addonAfter: <div className="add-on-after">đ</div>,
};

const style = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

function DrawerEditProduct({
  dataSelected,
  openDrawerEditProduct,
  handleCancelDrawerEditProduct,
  isAvailable,
  canUpdate,
  canUpdatePrice,
  onSoldSaved,
}) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isVAT, setIsVAT] = useState(true);
  const [formValues, setFormValues] = useState(canUpdatePrice ? form.getFieldsValue() : {});
  const { handleUpdateProduct, loading } = useUpdateProduct();
  const [visibleExportVat, setVisibleExportVat] = useState(false);
  const [visible, setVisible] = useState(false);
  const [enablePreOrderPolicy, setEnablePreOrderPolicy] = useState(false);

  const calcValueProduct = map(formValues?.productDiscounts, (productDiscount, index) => {
    const validRegionItems = dataSelected?.stocks?.filter((item) => item?.warehouse?.regionID !== null);

    const minValue = validRegionItems?.reduce((acc, stock) => acc + (stock?.quantity || 0), 0);
    return {
      quantity: productDiscount?.minQuantity,
      discountAmount: productDiscount?.discountAmount,
      smallestValueInStock: minValue || 0,
      smallestValueInPrice: formValues?.productPrices?.price,
    };
  });

  const initForm = {
    productPrices: {
      price: dataSelected?.productPrices?.price,
      priceVat: dataSelected?.productPrices?.priceVat,
    },
    vat: dataSelected?.vat || isVAT,
    vatPercent: [-1, 0, 3, 5, 8, 10].includes(dataSelected?.vatPercent) ? undefined : dataSelected?.vatPercent,
    vatValue: dataSelected?.vatValue > -1 ? dataSelected?.vatValue : 0,
    note: dataSelected?.note,
    productDiscounts: dataSelected?.productDiscounts?.map((item) => ({
      minQuantity: item?.minQuantity,
      discountAmount: item?.discountAmount,
    })),
    productPreOrderPolicy:
      dataSelected?.productPreOrderPolicies && dataSelected?.productPreOrderPolicies?.length > 0
        ? dataSelected?.productPreOrderPolicies?.map((item) => ({
            minOrderQuantity: item?.minOrderQuantity,
            preOrderPrice: item?.preOrderPrice,
            depositPaymentValue: item?.depositPaymentValue,
            warranty: item?.warranty,
            minDeliveryDays: item?.minDeliveryDays,
            maxDeliveryDays: item?.maxDeliveryDays,
          }))
        : [
            {
              minOrderQuantity: undefined,
              preOrderPrice: undefined,
              depositPaymentValue: undefined,
              warranty: undefined,
              minDeliveryDays: undefined,
              maxDeliveryDays: undefined,
            },
          ],
    vatType: dataSelected?.vatType,
    taxableGapVAT: dataSelected?.vatValue > -1,
    taxablePercentVAT:
      dataSelected?.vatPercent !== undefined && [-1, 0, 3, 5, 8, 10].includes(dataSelected?.vatPercent)
        ? dataSelected?.vatPercent
        : "OTHER",
    minOrderQuantity: dataSelected?.minOrderQuantity,
    fulfillmentMode: dataSelected?.fulfillmentMode,
    minDeliveryDays: dataSelected?.preOrderPolicyInfo?.minDeliveryDays,
    maxDeliveryDays: dataSelected?.preOrderPolicyInfo?.maxDeliveryDays,
    depositPaymentValue: dataSelected?.preOrderPolicyInfo?.depositPaymentValue,
    depositPaymentMethod: dataSelected?.preOrderPolicyInfo?.depositPaymentMethod,
    // Map productWarrantyPrices từ API (bỏ level 1 - tiêu chuẩn)
    productWarrantyPriceInput:
      dataSelected?.productWarrantyPrices
        ?.filter((item) => item.level !== 1)
        ?.map((item) => ({
          warranty: item?.warranty,
          discountAmount: item?.discountAmount,
          level: item?.level,
        })) || [],
  };

  const handleValuesChange = (changeValue, values) => {
    if (values) {
      setFormValues(values);

      // Chỉ sync khi fulfillmentMode là PRE_ORDER và KHÔNG có enablePreOrderPolicy
      // (tức là chỉ sync cho trường hợp hàng đặt trước thuần túy, không phải chính sách đặt trước)
      if (values?.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER && !enablePreOrderPolicy) {
        const currentPreOrderPolicy = values?.productPreOrderPolicy || [];

        // Nếu có thay đổi các trường liên quan
        if (
          changeValue?.minDeliveryDays !== undefined ||
          changeValue?.maxDeliveryDays !== undefined ||
          changeValue?.depositPaymentValue !== undefined ||
          changeValue?.minOrderQuantity !== undefined ||
          changeValue?.productPrices?.price !== undefined
        ) {
          // Update item đầu tiên
          if (currentPreOrderPolicy.length > 0) {
            const updatedPolicy = [...currentPreOrderPolicy];
            updatedPolicy[0] = {
              ...updatedPolicy[0],
              minOrderQuantity: values?.minOrderQuantity,
              preOrderPrice: values?.productPrices?.price,
              minDeliveryDays: values?.minDeliveryDays,
              maxDeliveryDays: values?.maxDeliveryDays,
              depositPaymentValue: values?.depositPaymentValue,
              warranty: convertWarrantyToMonths(dataSelected?.warranty, dataSelected?.warrantyUnit),
            };
            form.setFieldsValue({ productPreOrderPolicy: updatedPolicy });
          }
        }
      }
    }
  };

  const handleOpenExportVat = async () => {
    await form.validateFields();
    setVisibleExportVat(true);
  };

  const handleCancelExportVat = () => {
    setVisibleExportVat(false);
  };

  const handleSubmitEditProduct = async () => {
    if (!canUpdatePrice) return;
    const row = await form.validateFields();

    const price = row?.productPrices?.price;

    const allDiscountsGreaterThanPrice = row?.productDiscounts?.every((discount) => discount.discountAmount > price);

    const checkEnabledPreOrderPOlicy = row?.fulfillmentMode === "PRE_ORDER" ? true : enablePreOrderPolicy;
    if (checkEnabledPreOrderPOlicy && row?.productPreOrderPolicy?.length === 0) {
      notify.error({
        message: "Loại hàng đặt trước bắt buộc có chính sách giá đặt trước!",
      });
      return;
    }

    try {
      if (!allDiscountsGreaterThanPrice || !row?.productDiscounts?.length) {
        await handleUpdateProduct({
          productID: dataSelected?.id,
          vat: row.vat,
          vatValue: row.taxableGapVAT ? row.vatValue : -1,
          vatPercent: [-1, 0, 3, 5, 8, 10].includes(row?.taxablePercentVAT) ? row?.taxablePercentVAT : row?.vatPercent,
          prices: row.productPrices,
          note: row.note,
          productDiscounts: row?.productDiscounts?.every((discount) => discount.discountAmount)
            ? row?.productDiscounts
            : [],
          sellingStatus: isAvailable,
          vatType: row?.vatType,
          minOrderQuantity: row?.minOrderQuantity,
          fulfillmentMode: row.fulfillmentMode,
          productWarrantyPriceInput: [
            {
              warranty: getStandardWarrantyInMonths(dataSelected?.productWarrantyPrices),
              discountAmount: 0,
              level: 1,
            },

            ...(row?.productWarrantyPriceInput?.map((item, index) => ({
              ...item,
              level: index + 2,
            })) || []),
          ],
          // ...(row.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER && {
          //   preOrderPolicy: {
          //     minDeliveryDays: row.minDeliveryDays,
          //     maxDeliveryDays: row.maxDeliveryDays,
          //     depositPaymentMethod: DEPOSIT_PAYMENT_METHOD_TYPE.DEPOSIT_BY_PERCENT_VALUE,
          //     depositPaymentValue: row.depositPaymentValue,
          //   },
          // }),
          // Thêm productPreOrderPolicy - luôn truyền lên, nếu không có thì mảng rỗng
          productPreOrderPolicy:
            checkEnabledPreOrderPOlicy && row?.productPreOrderPolicy?.length > 0
              ? row.productPreOrderPolicy.map((item, index) => ({
                  minDeliveryDays: item?.minDeliveryDays,
                  maxDeliveryDays: item?.maxDeliveryDays,
                  depositPaymentMethod: DEPOSIT_PAYMENT_METHOD_TYPE.DEPOSIT_BY_PERCENT_VALUE,
                  depositPaymentValue: item?.depositPaymentValue,
                  level: index + 1,
                  minOrderQuantity: item?.minOrderQuantity,
                  preOrderPrice: item?.preOrderPrice,
                  warranty: item?.warranty,
                  warrantyUnit: "MONTH",
                }))
              : [],
          // Thêm canPreOrder API property - false nếu productPreOrderPolicy rỗng
          canPreOrder: enablePreOrderPolicy && row?.productPreOrderPolicy?.length > 0,
        });
        notify.success({
          message: t("product.validateMessage.updateSuccess"),
        });
        setVisible(false);
        setVisibleExportVat(false);
        handleCancelDrawerEditProduct();
      }
    } catch (errInfo) {
      notify.error({
        message: t("product.validateMessage.updateError"),
        description: errInfo?.message,
      });
    }
  };

  useEffect(() => {
    if (!canUpdatePrice) return;
    form.setFieldsValue({ ...initForm });
    setIsVAT(dataSelected?.vat);
    setFormValues(form.getFieldsValue());

    const hasPreOrderPolicy = !!(
      dataSelected?.productPreOrderPolicies && dataSelected?.productPreOrderPolicies?.length > 0
    );
    // Sử dụng canPreOrder từ API nếu có, nếu không thì dùng hasPreOrderPolicy
    const canPreOrderFromAPI = dataSelected?.canPreOrder !== undefined ? dataSelected?.canPreOrder : hasPreOrderPolicy;
    setEnablePreOrderPolicy(canPreOrderFromAPI);
  }, [dataSelected, openDrawerEditProduct, canUpdatePrice]);

  const optionfulfillmentMode = [
    { label: "Hàng đặt trước", value: PRODUCT_FULFILLMENT_MODE.PRE_ORDER },
    { label: "Hàng sẵn bán", value: PRODUCT_FULFILLMENT_MODE.IN_STOCK },
  ];

  const optionVatPercent = [
    { value: 8, label: "8%" },
    { value: 10, label: "10%" },
    { value: 0, label: "0%" },
    { value: 3, label: "3%" },
    { value: 5, label: "5%" },
    { value: -1, label: t("v2.product.drawer_edit_product.not_taxable") },
    { value: "OTHER", label: "Khác" },
  ];

  if (!canUpdatePrice) {
    return (
      <CustomDrawer
        visible={openDrawerEditProduct}
        title={t("product.edit")}
        onClose={handleCancelDrawerEditProduct}
        buttonOk={<span />}
        width={800}
        bodyStyle={{ background: "#F7F8FB" }}
        headerSticky={<CustomCard title={dataSelected?.code} content={dataSelected?.name} />}
        content={
          <ProductSoldDisplay
            product={dataSelected}
            active={openDrawerEditProduct}
            canUpdate={canUpdate}
            onSaved={onSoldSaved}
          />
        }
      />
    );
  }

  return (
    <CustomDrawer
      visible={openDrawerEditProduct}
      title={t("product.edit")}
      onOk={() => {
        form.validateFields().then(() => {
          if (
            (dataSelected?.stocks === null ||
              (dataSelected?.stocks && dataSelected?.stocks[0]?.quantityRealStockHasVat === 0)) &&
            formValues?.vat === true
          ) {
            handleOpenExportVat();
          } else {
            if (
              formValues?.vatType === VAT_TYPE.REQUIRED &&
              (dataSelected?.stocks === null ||
                (dataSelected?.stocks && dataSelected?.stocks[0]?.quantityRealStockHasVat === 0))
            ) {
              setVisible(true);
            } else {
              handleSubmitEditProduct();
            }
          }
        });
      }}
      onClose={() => handleCancelDrawerEditProduct()}
      loading={loading}
      bodyStyle={{ background: "#F7F8FB" }}
      width={800}
      buttonCancel={
        <CustomModal
          customComponent={<CustomButton isSecondary title={t("common.cancel")} />}
          icon={Img.QuestionIcon}
          message={t("product.pricePolicy.messageCloseDrawer")}
          footer={false}
          onOke={() => handleCancelDrawerEditProduct()}
        />
      }
      headerSticky={
        <Card>
          <Row style={{ flexWrap: "nowrap", alignItems: "center" }}>
            <Col span={5}>
              <Typography.Text strong>{t("common.product")}</Typography.Text>
            </Col>
            <Col>
              <Space direction="vertical" size={[0]}>
                <CustomTextHiddenLine
                  isLink={true}
                  strong={"strong"}
                  text={dataSelected?.code}
                  link={`product/detail/${dataSelected?.id}`}
                />
                <CustomTextHiddenLine text={dataSelected?.name} />
              </Space>
            </Col>
          </Row>
        </Card>
      }
      content={
        <Form
          initialValues={initForm}
          className="custom-drawer-edit-product"
          form={form}
          colon={false}
          layout="vertical"
          onValuesChange={handleValuesChange}
        >
          <CustomModalExportVat
            handleOpenExportVat={handleOpenExportVat}
            handleCancelExportVat={handleCancelExportVat}
            visibleExportVat={visibleExportVat}
            handleSubmitEditProduct={handleSubmitEditProduct}
            loading={loading}
            productCode={dataSelected?.code}
          />
          <CustomModal
            visible={visible}
            icon={Img.QuestionIcon}
            message={
              <>
                <div>
                  Mã sản phẩm <Typography.Link>{dataSelected?.code}</Typography.Link> có{" "}
                  <Typography.Link>tồn VAT = 0</Typography.Link>.
                </div>
                <div>Bạn có chắn chắn xuất VAT cho mã sản phẩm này không?</div>
              </>
            }
            footer={false}
            title={<Typography.Text strong>Xác nhận xuất VAT</Typography.Text>}
            onOke={() => handleSubmitEditProduct()}
            onCancel={() => setVisible(false)}
          />

          <div style={style}>
            <div className="vatInfo">
              <CustomCard
                title={
                  <Typography.Text strong style={{ color: "#052987" }}>
                    Thông tin về giá
                  </Typography.Text>
                }
                content={
                  <div>
                    <Row gutter={[24, 0]} className="vat-info-container">
                      <Col span={12}>
                        <FormInput
                          type={INPUT_TYPE.CURRENCY_INPUT}
                          formItemOptions={{
                            name: ["productPrices", "price"],
                            rules: [
                              {
                                required: true,
                                message: "Vui lòng không được để trống trường này!",
                              },
                              {
                                type: "number",
                                min: 1,
                                message: t("campaign.combo.errorMessage.valueMustBeThan0"),
                              },
                              validator({
                                type: "number",
                              }),
                            ],
                            label: <Typography.Text strong>{"Giá có VAT"}</Typography.Text>,
                          }}
                          inputOptions={{
                            ...inputOptionsPrices,
                            placeholder: "Nhập giá có VAT",
                          }}
                        />
                      </Col>
                      <Col span={12}>
                        <Row className="vat-value-group">
                          <FormInput
                            type={INPUT_TYPE.SELECT}
                            formItemOptions={{
                              style: { minWidth: "130px" },
                              rules: [
                                {
                                  required: true,
                                  message: t("common.messageRequiredType", {
                                    message: t("product.tableTitle.vatValue"),
                                  }),
                                },
                              ],
                              name: "taxablePercentVAT",
                              label: <Typography.Text strong>Loại % VAT</Typography.Text>,
                            }}
                            inputOptions={{
                              options: optionVatPercent,
                              disabled: !(formValues?.vatType !== VAT_TYPE.NOT_REQUIRED),
                              defaultValue: -1,
                            }}
                          />
                          <FormInput
                            type={INPUT_TYPE.NUMBER}
                            key={formValues?.taxablePercentVAT}
                            formItemOptions={{
                              rules: [
                                {
                                  required: ![-1, 0, 3, 5, 8, 10].includes(formValues?.taxablePercentVAT),
                                  message: t("common.messageRequiredType", {
                                    message: t("product.productForm.percentVAT"),
                                  }),
                                },
                              ],
                              name: "vatPercent",
                              className: "vat",
                            }}
                            inputOptions={{
                              min: 0,
                              max: 100,
                              addonAfter: <div className="add-on-after">%</div>,
                              placeholder: t("product.productForm.percentVATPlaceholder"),
                              disabled: [-1, 0, 3, 5, 8, 10].includes(formValues?.taxablePercentVAT),
                              maxLength: 3,
                            }}
                          />
                        </Row>
                      </Col>
                    </Row>
                  </div>
                }
              />
            </div>
            <div className="edit_general_info">
              <CustomCard
                title={
                  <Typography.Text strong style={{ color: "#052987" }}>
                    Điều kiện bán hàng
                  </Typography.Text>
                }
                content={
                  <Row gutter={[24, 0]} className="price-update-container">
                    <Col span={12}>
                      <FormInput
                        type={INPUT_TYPE.SELECT}
                        formItemOptions={{
                          rules: [
                            {
                              required: true,
                            },
                          ],
                          name: "fulfillmentMode",
                          label: <Typography.Text strong>Loại hàng</Typography.Text>,
                        }}
                        inputOptions={{
                          options: optionfulfillmentMode,
                        }}
                      />
                    </Col>

                    <Col span={12}>
                      <FormInput
                        type={INPUT_TYPE.NUMBER}
                        formItemOptions={{
                          name: "minOrderQuantity",
                          required: true,
                          rules: [
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value === null || value === undefined || value === "") {
                                  return Promise.reject(new Error("Vui lòng nhập mức MOQ"));
                                }
                                if (!/^\d+$/.test(value) || Number(value) < 1) {
                                  return Promise.reject(new Error("Vui lòng nhập mức MOQ là số nguyên dương"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ],
                          label: <Typography.Text strong>Mức MOQ</Typography.Text>,
                        }}
                        inputOptions={{
                          onChange: () => {
                            // Trigger validation cho các MOQ trong bảng chính sách khi MOQ sản phẩm thay đổi
                            setTimeout(() => {
                              const formValues = form.getFieldsValue();
                              const productPreOrderPolicy = formValues?.productPreOrderPolicy || [];

                              productPreOrderPolicy.forEach((_, idx) => {
                                form.validateFields([["productPreOrderPolicy", idx, "minOrderQuantity"]]).catch();
                              });
                            }, 100);
                          },
                        }}
                      />
                    </Col>
                  </Row>
                }
              />
            </div>
            <div className="warrantyPriceUpdate">
              <CustomCard
                title={
                  <Typography.Text strong style={{ color: "#052987" }}>
                    Cập nhật giá theo thời gian bảo hành
                  </Typography.Text>
                }
                content={
                  <Form.List name="productWarrantyPriceInput">
                    {(fields, { add, remove }) => (
                      <>
                        <Row
                          gutter={[16, 0]}
                          style={{
                            background: "#F7F8FB",
                            padding: "12px 16px",
                            borderRadius: "8px 8px 0 0",
                            marginBottom: 0,
                          }}
                        >
                          <Col span={2} style={{ textAlign: "center" }}>
                            <Typography.Text strong style={{ fontSize: 12, color: "#5B6673" }}>
                              MỨC
                            </Typography.Text>
                          </Col>
                          <Col span={6} style={{ textAlign: "center" }}>
                            <Typography.Text strong style={{ fontSize: 12, color: "#5B6673" }}>
                              THỜI GIAN BẢO HÀNH (THÁNG)
                            </Typography.Text>
                          </Col>
                          <Col span={10} style={{ textAlign: "center" }}>
                            <Typography.Text strong style={{ fontSize: 12, color: "#5B6673" }}>
                              MỨC GIẢM THEO THỜI GIAN BẢO HÀNH/ 1 SẢN PHẨM
                            </Typography.Text>
                          </Col>
                          <Col span={4} style={{ textAlign: "center" }}>
                            <Typography.Text strong style={{ fontSize: 12, color: "#5B6673" }}>
                              GIÁ
                            </Typography.Text>
                          </Col>
                          <Col span={2}></Col>
                        </Row>

                        {/* Row tiêu chuẩn - Mức 1 (không xóa được) */}
                        <Row
                          gutter={[16, 0]}
                          align="middle"
                          style={{
                            padding: "16px",
                            borderBottom: "1px solid #E8E8E8",
                          }}
                        >
                          <Col span={2} style={{ textAlign: "center" }}>
                            <Typography.Text>1</Typography.Text>
                          </Col>
                          <Col span={6} style={{ textAlign: "center" }}>
                            <div>
                              <Typography.Text strong>
                                {getStandardWarrantyInMonths(dataSelected?.productWarrantyPrices)}
                              </Typography.Text>
                              <br />
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                (tiêu chuẩn)
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col span={10} style={{ textAlign: "center" }}>
                            <Typography.Text>---</Typography.Text>
                          </Col>
                          <Col span={4} style={{ textAlign: "center" }}>
                            <Typography.Text>
                              {(() => {
                                const basePrice = formValues?.productPrices?.price || 0;
                                return basePrice.toLocaleString("vi-VN");
                              })()}
                              đ
                            </Typography.Text>
                          </Col>
                          <Col span={2}></Col>
                        </Row>

                        {/* Các row động - Mức 2, 3 */}
                        {fields.map((field, index) => {
                          const discountAmount = form.getFieldValue([
                            "productWarrantyPriceInput",
                            field.name,
                            "discountAmount",
                          ]);
                          const basePrice = formValues?.productPrices?.price || 0;

                          const calculatedPrice = basePrice - (discountAmount || 0);

                          return (
                            <Row
                              key={field.key}
                              gutter={[16, 0]}
                              align="middle"
                              style={{
                                padding: "16px",
                                borderBottom: "1px solid #E8E8E8",
                              }}
                            >
                              <Col span={2} style={{ textAlign: "center" }}>
                                <Typography.Text>{index + 2}</Typography.Text>
                              </Col>
                              <Col span={6}>
                                <FormInput
                                  type={INPUT_TYPE.NUMBER}
                                  formItemOptions={{
                                    name: [field.name, "warranty"],
                                    rules: [
                                      { required: true, message: "Vui lòng nhập" },
                                      {
                                        validator: (_, value) => {
                                          // Kiểm tra giá trị tối thiểu là 0
                                          if (value !== undefined && value !== null && value < 0) {
                                            return Promise.reject(new Error("Giá trị phải ≥ 0 tháng"));
                                          }

                                          // Lấy thời gian bảo hành tiêu chuẩn (đã quy đổi về tháng)
                                          const standardWarranty = getStandardWarrantyInMonths(
                                            dataSelected?.productWarrantyPrices
                                          );

                                          // Lấy thời gian bảo hành của mức trước realtime
                                          let prevWarranty;
                                          if (index === 0) {
                                            prevWarranty = standardWarranty;
                                          } else {
                                            prevWarranty =
                                              form.getFieldValue([
                                                "productWarrantyPriceInput",
                                                index - 1,
                                                "warranty",
                                              ]) || standardWarranty;
                                          }

                                          if (value !== undefined && value !== null && value >= prevWarranty) {
                                            return Promise.reject(new Error(`Phải nhỏ hơn ${prevWarranty} tháng`));
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ],
                                    style: { marginBottom: 0 },
                                  }}
                                  inputOptions={{
                                    placeholder: "Nhập",
                                    style: { width: "100%" },
                                  }}
                                />
                              </Col>
                              <Col span={10}>
                                <FormInput
                                  type={INPUT_TYPE.CURRENCY_INPUT}
                                  formItemOptions={{
                                    name: [field.name, "discountAmount"],
                                    rules: [
                                      { required: true, message: "Vui lòng nhập" },
                                      {
                                        validator: (_, value) => {
                                          if (value !== undefined && value !== null && value > basePrice) {
                                            return Promise.reject(
                                              new Error(`Mức giảm tối đa là ${basePrice.toLocaleString("vi-VN")}đ`)
                                            );
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ],
                                    style: { marginBottom: 0 },
                                  }}
                                  inputOptions={{
                                    placeholder: "Nhập mức giảm",
                                    style: { width: "100%" },
                                    addonAfter: <div className="add-on-after">đ</div>,
                                    min: 0,
                                  }}
                                />
                              </Col>
                              <Col span={4} style={{ textAlign: "center" }}>
                                <Typography.Text>
                                  {calculatedPrice > 0 ? calculatedPrice.toLocaleString("vi-VN") : 0}đ
                                </Typography.Text>
                              </Col>
                              <Col span={2} style={{ textAlign: "center" }}>
                                <DeleteOutlined
                                  style={{ color: "#5B6673", cursor: "pointer" }}
                                  onClick={() => remove(field.name)}
                                />
                              </Col>
                            </Row>
                          );
                        })}

                        {/* Chỉ hiển thị button khi số mức < 3 (tức fields.length < 2) */}
                        {fields.length < 2 && (
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined style={{ color: "#2246DC" }} />}
                            style={{
                              marginTop: 16,
                              borderColor: "#2246DC",
                              color: "#2246DC",
                              backgroundColor: "#fff",
                            }}
                          >
                            Thêm giá theo bảo hành
                          </Button>
                        )}
                      </>
                    )}
                  </Form.List>
                }
              />
            </div>
            <ProductSoldDisplay
              product={dataSelected}
              active={openDrawerEditProduct}
              canUpdate={canUpdate}
              onSaved={onSoldSaved}
            />
            <div className="pricePolicyUpdate">
              <CustomCard
                title={
                  <Typography.Text strong style={{ color: "#052987" }}>
                    Chính sách giá theo số lượng hàng sẵn kho
                  </Typography.Text>
                }
                content={
                  <Form.List name="productDiscounts">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => {
                          const isValidateHelpQty =
                            calcValueProduct?.[index]?.quantity > calcValueProduct?.[index]?.smallestValueInStock ||
                            calcValueProduct?.[index]?.quantity < formValues?.minOrderQuantity;

                          const isValidateHelpPrice =
                            calcValueProduct?.[index]?.discountAmount > calcValueProduct?.[index]?.smallestValueInPrice;

                          return (
                            <div key={field?.key}>
                              <Space style={{ width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <Typography.Text strong>
                                    {t("product.pricePolicy.level")} {index + 1}
                                  </Typography.Text>
                                </div>
                                <div>
                                  <Space>
                                    <Typography.Text>{t("product.pricePolicy.buyFrom")}</Typography.Text>
                                    <FormInput
                                      type={INPUT_TYPE.NUMBER}
                                      {...{
                                        formItemOptions: {
                                          name: [field?.name, "minQuantity"],
                                          validateStatus: (() => {
                                            const minOrderQty = formValues?.minOrderQuantity;
                                            const quantity = calcValueProduct?.[index]?.quantity;
                                            if (quantity < minOrderQty) {
                                              return "error";
                                            }
                                            if (isValidateHelpQty) {
                                              return "warning";
                                            }
                                            return undefined;
                                          })(),
                                          help: (() => {
                                            const minOrderQty = formValues?.minOrderQuantity;
                                            const quantity = calcValueProduct?.[index]?.quantity;
                                            if (quantity < minOrderQty) {
                                              return `Vui lòng nhập số lượng ≥ mức MOQ`;
                                            }
                                            if (isValidateHelpQty) {
                                              return t("cart.lackOfStock");
                                            }
                                            return undefined;
                                          })(),
                                          rules: [
                                            {
                                              required: true,
                                              message: t("campaign.combo.errorMessage.qty"),
                                            },

                                            validator({
                                              type: "number",
                                            }),
                                          ],
                                        },
                                        inputOptions: {
                                          placeholder: t("campaign.combo.placeholder.type"),
                                          className: "input-number-qty",
                                          maxLength: 10,
                                        },
                                      }}
                                    />

                                    <Typography.Text>{t("product.pricePolicy.product")}</Typography.Text>
                                    <FormInput
                                      {...{
                                        type: INPUT_TYPE.CURRENCY_INPUT,
                                        formItemOptions: {
                                          name: [field?.name, "discountAmount"],
                                          validateStatus: isValidateHelpPrice ? "error" : undefined,
                                          help: isValidateHelpPrice
                                            ? t("product.pricePolicy.invalidDiscount")
                                            : undefined,
                                          rules: [
                                            {
                                              required: true,
                                              message: t("campaign.combo.errorMessage.typeDiscount"),
                                            },
                                            {
                                              type: "number",
                                              min: 1,
                                              message: t("campaign.combo.errorMessage.valueMustBeThan0"),
                                            },
                                            validator({
                                              type: "number",
                                            }),
                                          ],
                                        },
                                        inputOptions: {
                                          className: "input-number",
                                          placeholder: t("campaign.combo.placeholder.type"),
                                        },
                                      }}
                                    />
                                    <Typography.Text>{t("product.pricePolicy.onProduct")}</Typography.Text>
                                  </Space>
                                </div>
                                <div>
                                  <DeleteOutlined onClick={() => remove(field?.name)} />
                                </div>
                              </Space>
                              <Divider style={{ margin: "12px 0" }} />
                            </div>
                          );
                        })}
                        {fields?.length < 4 && (
                          <Button
                            type="dashed"
                            onClick={() => {
                              add();
                            }}
                            block
                            icon={<PlusOutlined />}
                          >
                            {t("product.pricePolicy.additionalIncentives")}
                          </Button>
                        )}
                      </>
                    )}
                  </Form.List>
                }
              />
            </div>

            {/* Checkbox cho phép đặt trước sản phẩm - ngoài card */}
            <div style={{ marginBottom: "0px" }}>
              <Checkbox
                checked={
                  formValues?.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER ? true : enablePreOrderPolicy
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEnablePreOrderPolicy(checked);
                  if (!checked) {
                    // Clear form data when unchecked
                    form.setFieldsValue({ productPreOrderPolicy: [] });
                  } else {
                    // Add default item when checked
                    form.setFieldsValue({
                      productPreOrderPolicy: [
                        {
                          minOrderQuantity: undefined,
                          preOrderPrice: undefined,
                          depositPaymentValue: undefined,
                          warranty: undefined,
                          minDeliveryDays: undefined,
                          maxDeliveryDays: undefined,
                        },
                      ],
                    });
                  }
                }}
                disabled={formValues?.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER}
                style={{
                  opacity: formValues?.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER ? 0.6 : 1,
                }}
              >
                <div>
                  <Typography.Text strong>Cho phép đặt trước sản phẩm</Typography.Text>
                </div>
              </Checkbox>
            </div>

            <div className="productPreOrderPolicy">
              {(enablePreOrderPolicy || formValues?.fulfillmentMode === PRODUCT_FULFILLMENT_MODE.PRE_ORDER) && (
                <CustomCard
                  title={
                    <Typography.Text strong style={{ color: "#052987" }}>
                      Chính sách giá theo số lượng hàng đặt trước
                    </Typography.Text>
                  }
                  content={
                    <Form.List name="productPreOrderPolicy">
                      {(fields, { add, remove }) => (
                        <div style={{ width: "100%" }}>
                          {/* Table Header */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "50px 90px 110px 100px 120px 150px 40px",
                              gap: "8px",
                              padding: "12px 8px",
                              backgroundColor: "#f0f2f5",
                              borderRadius: "4px 4px 0 0",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#5B6673",
                              alignItems: "center",
                            }}
                          >
                            <div style={{ textAlign: "center" }}>MỨC</div>
                            <div style={{ textAlign: "center" }}>MỨC MOQ</div>
                            <div style={{ textAlign: "center" }}>TỶ LỆ CỌC (%)</div>
                            <div style={{ textAlign: "center" }}>
                              THỜI GIAN
                              <br />
                              BẢO HÀNH
                              <br />
                              (THÁNG)
                            </div>
                            <div style={{ textAlign: "center" }}>
                              THỜI GIAN GIAO
                              <br />
                              HÀNG DỰ KIẾN
                              <br />
                              (SỐ NGÀY)
                            </div>
                            <div style={{ textAlign: "center" }}>GIÁ ĐẶT TRƯỚC</div>
                            <div></div>
                          </div>

                          {/* Table Rows */}
                          {fields.map((field, index) => {
                            const isValidateHelpQty =
                              calcValueProduct?.[index]?.quantity > calcValueProduct?.[index]?.smallestValueInStock ||
                              calcValueProduct?.[index]?.quantity < formValues?.minOrderQuantity;

                            const isValidateHelpPrice =
                              calcValueProduct?.[index]?.discountAmount >
                              calcValueProduct?.[index]?.smallestValueInPrice;

                            return (
                              <div
                                key={field?.key}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "50px 90px 110px 100px 120px 150px 40px",
                                  gap: "8px",
                                  padding: "16px 8px",
                                  borderBottom: "1px solid #f0f0f0",
                                  alignItems: "start",
                                }}
                              >
                                {/* Mức */}
                                <div style={{ textAlign: "center", paddingTop: "8px" }}>
                                  <Typography.Text strong>{index + 1}</Typography.Text>
                                </div>

                                {/* Mức MOQ */}
                                <div>
                                  <FormInput
                                    type={INPUT_TYPE.NUMBER}
                                    {...{
                                      formItemOptions: {
                                        name: [field?.name, "minOrderQuantity"],
                                        style: { marginBottom: 0 },
                                        validateStatus: (() => {
                                          const minOrderQty = formValues?.minOrderQuantity;
                                          const quantity = calcValueProduct?.[index]?.quantity;
                                          if (quantity < minOrderQty) {
                                            return "error";
                                          }
                                          if (isValidateHelpQty) {
                                            return "warning";
                                          }
                                          return undefined;
                                        })(),
                                        help: (() => {
                                          const minOrderQty = formValues?.minOrderQuantity;
                                          const quantity = calcValueProduct?.[index]?.quantity;
                                          if (quantity < minOrderQty) {
                                            return `≥ MOQ`;
                                          }
                                          if (isValidateHelpQty) {
                                            return t("cart.lackOfStock");
                                          }
                                          return undefined;
                                        })(),
                                        rules: [
                                          {
                                            required: true,
                                            message: "Vui lòng nhập mức MOQ",
                                          },
                                          validator({
                                            type: "number",
                                          }),
                                          {
                                            validator: (_, value) => {
                                              const formValues = form.getFieldsValue();
                                              const productPreOrderPolicy = formValues?.productPreOrderPolicy || [];
                                              const productMOQ = Number(formValues?.minOrderQuantity);

                                              if (!value || productPreOrderPolicy.length === 0) {
                                                return Promise.resolve();
                                              }

                                              const currentMOQ = Number(value);

                                              if (productMOQ && currentMOQ < productMOQ) {
                                                return Promise.reject(new Error(`MOQ chính sách phải ≥ MOQ sản phẩm`));
                                              }

                                              // Validate MOQ levels: level 1 < level 2 < level 3
                                              if (index === 0) {
                                                // Level 1: must be less than level 2
                                                const level2MOQ = Number(productPreOrderPolicy[1]?.minOrderQuantity);
                                                if (level2MOQ && currentMOQ >= level2MOQ) {
                                                  return Promise.reject(new Error("Mức 1 phải nhỏ hơn mức 2"));
                                                }
                                              } else if (index === 1) {
                                                // Level 2: must be greater than level 1 and less than level 3
                                                const level1MOQ = Number(productPreOrderPolicy[0]?.minOrderQuantity);
                                                const level3MOQ = Number(productPreOrderPolicy[2]?.minOrderQuantity);

                                                if (level1MOQ && currentMOQ <= level1MOQ) {
                                                  return Promise.reject(new Error("Mức 2 phải lớn hơn mức 1"));
                                                }
                                                if (level3MOQ && currentMOQ >= level3MOQ) {
                                                  return Promise.reject(new Error("Mức 2 phải nhỏ hơn mức 3"));
                                                }
                                              } else if (index === 2) {
                                                // Level 3: must be greater than level 2
                                                const level2MOQ = Number(productPreOrderPolicy[1]?.minOrderQuantity);
                                                if (level2MOQ && currentMOQ <= level2MOQ) {
                                                  return Promise.reject(new Error("Mức 3 phải lớn hơn mức 2"));
                                                }
                                              }

                                              return Promise.resolve();
                                            },
                                          },
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Nhập mức MOQ",
                                        style: { width: "100%" },
                                        maxLength: 4,
                                        onChange: (e) => {
                                          // Trigger validation cho các field MOQ khác khi thay đổi
                                          setTimeout(() => {
                                            const fieldNames = [];
                                            if (index !== 0)
                                              fieldNames.push(["productPreOrderPolicy", 0, "minOrderQuantity"]);
                                            if (index !== 1)
                                              fieldNames.push(["productPreOrderPolicy", 1, "minOrderQuantity"]);
                                            if (index !== 2)
                                              fieldNames.push(["productPreOrderPolicy", 2, "minOrderQuantity"]);

                                            fieldNames.forEach((fieldName) => {
                                              form.validateFields([fieldName]).catch();
                                            });
                                          }, 100);
                                        },
                                      },
                                    }}
                                  />
                                </div>

                                {/* Tỷ lệ cọc */}
                                <div>
                                  <FormInput
                                    type={INPUT_TYPE.NUMBER}
                                    {...{
                                      formItemOptions: {
                                        name: [field?.name, "depositPaymentValue"],
                                        style: { marginBottom: 0 },
                                        rules: [
                                          {
                                            required: false,
                                          },
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Nhập tỷ lệ cọc",
                                        style: { width: "100%" },
                                        min: 0,
                                        max: 100,
                                      },
                                    }}
                                  />
                                </div>

                                {/* Thời gian bảo hành */}
                                <div>
                                  <FormInput
                                    type={INPUT_TYPE.NUMBER}
                                    {...{
                                      formItemOptions: {
                                        name: [field?.name, "warranty"],
                                        style: { marginBottom: 0 },
                                        rules: [
                                          {
                                            required: true,
                                            message: "Vui lòng nhập thời gian bảo hành",
                                          },
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Nhập thời gian bảo hành",
                                        style: { width: "100%" },
                                        min: 0,
                                      },
                                    }}
                                  />
                                </div>

                                {/* Thời gian giao hàng */}
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <FormInput
                                    type={INPUT_TYPE.NUMBER}
                                    {...{
                                      formItemOptions: {
                                        name: [field?.name, "minDeliveryDays"],
                                        style: { marginBottom: 0 },
                                        rules: [
                                          {
                                            required: true,
                                            message: "Vui lòng nhập thời gian giao hàng dự kiến",
                                          },
                                          ({ getFieldValue }) => ({
                                            validator(_, value) {
                                              const maxDays = getFieldValue([
                                                "productPreOrderPolicy",
                                                field?.name,
                                                "maxDeliveryDays",
                                              ]);
                                              if (value && maxDays && value > maxDays) {
                                                return Promise.reject(
                                                  new Error(
                                                    "Số ngày giao hàng sớm nhất phải ≤ số ngày giao hàng muộn nhất"
                                                  )
                                                );
                                              }
                                              return Promise.resolve();
                                            },
                                          }),
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Min",
                                        style: { width: "100%" },
                                        min: 0,
                                        onChange: () => {
                                          // Trigger validation for maxDeliveryDays
                                          form.validateFields([
                                            ["productPreOrderPolicy", field?.name, "maxDeliveryDays"],
                                          ]);
                                        },
                                      },
                                    }}
                                  />
                                  <span style={{ color: "#8C8C8C" }}>-</span>
                                  <FormInput
                                    type={INPUT_TYPE.NUMBER}
                                    {...{
                                      formItemOptions: {
                                        name: [field?.name, "maxDeliveryDays"],
                                        style: { marginBottom: 0 },
                                        rules: [
                                          {
                                            required: true,
                                            message: "Vui lòng nhập thời gian giao hàng dự kiến",
                                          },
                                          ({ getFieldValue }) => ({
                                            validator(_, value) {
                                              const minDays = getFieldValue([
                                                "productPreOrderPolicy",
                                                field?.name,
                                                "minDeliveryDays",
                                              ]);
                                              if (value && minDays && value < minDays) {
                                                return Promise.reject(
                                                  new Error(
                                                    "Vui lòng nhập Số ngày giao hàng muộn nhất phải ≥ số ngày giao hàng sớm nhất."
                                                  )
                                                );
                                              }
                                              return Promise.resolve();
                                            },
                                          }),
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Max",
                                        style: { width: "100%" },
                                        min: 0,
                                        onChange: () => {
                                          // Trigger validation for minDeliveryDays
                                          form.validateFields([
                                            ["productPreOrderPolicy", field?.name, "minDeliveryDays"],
                                          ]);
                                        },
                                      },
                                    }}
                                  />
                                </div>

                                {/* Giá đặt trước */}
                                <div>
                                  <FormInput
                                    {...{
                                      type: INPUT_TYPE.CURRENCY_INPUT,
                                      formItemOptions: {
                                        name: [field?.name, "preOrderPrice"],
                                        style: { marginBottom: 0 },
                                        validateStatus: isValidateHelpPrice ? "error" : undefined,
                                        help: isValidateHelpPrice ? "Giá không hợp lệ" : undefined,
                                        rules: [
                                          {
                                            required: true,
                                            message: "Vui lòng nhập giá đặt trước",
                                          },
                                          {
                                            type: "number",
                                            min: 1,
                                            message: "Phải > 0",
                                          },
                                          validator({
                                            type: "number",
                                          }),
                                        ],
                                      },
                                      inputOptions: {
                                        placeholder: "Nhập giá đặt trước",
                                        style: { width: "100%" },
                                      },
                                    }}
                                  />
                                </div>

                                {/* Delete button */}
                                <div style={{ textAlign: "center", paddingTop: "8px" }}>
                                  <DeleteOutlined
                                    onClick={() => remove(field?.name)}
                                    style={{ cursor: "pointer", color: "#ff4d4f", fontSize: "16px" }}
                                  />
                                </div>
                              </div>
                            );
                          })}

                          {/* Add button */}
                          {fields?.length < 3 && (
                            <div style={{ padding: "16px 0" }}>
                              <Button
                                type="dashed"
                                onClick={() => {
                                  add();
                                }}
                                block
                                icon={<PlusOutlined />}
                                style={{ height: "40px" }}
                              >
                                Thêm mức chính sách
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Form.List>
                  }
                />
              )}
            </div>
            <div className="note">
              <CustomCard
                title={
                  <Typography.Text strong style={{ color: "#052987" }}>
                    {t("product.tableTitle.note")}
                  </Typography.Text>
                }
                content={
                  <FormInput
                    type={INPUT_TYPE.TEXT_AREA}
                    formItemOptions={{ name: "note" }}
                    inputOptions={{
                      placeholder: t("product.tableTitle.note"),
                      autoSize: { minRows: 2 },
                    }}
                  />
                }
              />
            </div>
          </div>
        </Form>
      }
    />
  );
}

export default DrawerEditProduct;
